// ══════════════════════════════════════════════════════════════
// COMPASS – Root Application State
// ══════════════════════════════════════════════════════════════
import { ActionReducer, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { userReducer } from './user/user.reducer';
import { UserState, initialUserState } from './user/user.state';
import { managementCompanyReducer } from './management-company/management-company.reducer';
import { ManagementCompanyState, initialManagementCompanyState } from './management-company/management-company.state';
import { fundReducer } from './fund/fund.reducer';
import { FundState, initialFundState } from './fund/fund.state';
import { boardMemberReducer } from './board-member/board-member.reducer';
import { BoardMemberState, initialBoardMemberState } from './board-member/board-member.state';
import { countryReducer } from './country/country.reducer';
import { CountryState, initialCountryState } from './country/country.state';
import { processReducer } from './process/process.reducer';
import { ProcessState, initialProcessState } from './process/process.state';
import { notificationReducer } from './notification/notification.reducer';
import { NotificationState, initialNotificationState } from './notification/notification.state';
import { userSignatureReducer } from './user-signature/user-signature.reducer';
import { UserSignatureState, initialUserSignatureState } from './user-signature/user-signature.state';
import { UserActions } from './user/user.actions';

export interface AppState {
  user: UserState;
  managementCompany: ManagementCompanyState;
  fund: FundState;
  boardMember: BoardMemberState;
  process: ProcessState;
  country: CountryState;
  notification: NotificationState;
  userSignature: UserSignatureState;
}

export const appReducers: ActionReducerMap<AppState> = {
  user: userReducer,
  managementCompany: managementCompanyReducer,
  fund: fundReducer,
  boardMember: boardMemberReducer,
  process: processReducer,
  country: countryReducer,
  notification: notificationReducer,
  userSignature: userSignatureReducer,
};

export const initialAppState: AppState = {
  user: initialUserState,
  managementCompany: initialManagementCompanyState,
  fund: initialFundState,
  boardMember: initialBoardMemberState,
  process: initialProcessState,
  country: initialCountryState,
  notification: initialNotificationState,
  userSignature: initialUserSignatureState,
};

/**
 * Meta-reducer that resets ALL feature slices to their initial state when
 * `UserActions.sessionExpired` fires. Prevents stale data from leaking into
 * the next session after a forced logout / token failure.
 */
export function clearStateOnSessionExpired(
  reducer: ActionReducer<AppState>
): ActionReducer<AppState> {
  return (state, action) => {
    if (action.type === UserActions.sessionExpired.type) {
      return reducer(undefined, action);
    }
    return reducer(state, action);
  };
}

export const appMetaReducers: MetaReducer<AppState>[] = [clearStateOnSessionExpired];
